import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Tree, TreeNode } from "react-organizational-chart";
import { FaUser, FaChartLine } from "react-icons/fa";

import DebounceInput from "../../components/DebounceInput";
import UserCard from "./UserCard";
import Loader from "../../components/loader";
import LoadingButton from "../../components/loadingButton";
import api from "../../services/api";
import Pagination from "../../components/pagination";
import useStore from "../../services/store";

const VIEWS = {
  LIST: "list",
  ORGANIGRAM: "organigram",
  OFFICE: "office",
};

export default function List() {
  const [viewType, setViewType] = useState(VIEWS.LIST);

  return (
    <div className="p-4">
      <div className="flex justify-start mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          <ViewButton active={viewType === VIEWS.LIST} onClick={() => setViewType(VIEWS.LIST)}>List</ViewButton>
          <ViewButton active={viewType === VIEWS.ORGANIGRAM} onClick={() => setViewType(VIEWS.ORGANIGRAM)}>Organigram</ViewButton>
          <ViewButton active={viewType === VIEWS.OFFICE} onClick={() => setViewType(VIEWS.OFFICE)}>Office</ViewButton>
        </div>
      </div>

      {viewType === VIEWS.LIST && <ListView />}
      {viewType === VIEWS.ORGANIGRAM && <OrganigramView />}
      {viewType === VIEWS.OFFICE && <OfficeView />}
    </div>
  );
}

const ViewButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 text-sm rounded ${active ? "text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
    style={active ? { backgroundColor: "#fff48b" } : {}}>
    {children}
  </button>
);

/* ------------------------------------------------------------------ */
/* LIST VIEW (former People Pool)                                      */
/* ------------------------------------------------------------------ */

function ListView() {
  const [filter, setFilter] = useState({ search: "", per_page: 50, page: 1, status: "active", organisations: [], contracts: [], roles: ["user", "admin", "super_admin"] });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  async function fetchUsers() {
    try {
      setLoading(true);
      const { data } = await api.post(`/user/search`, { ...filter });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) {
      toast.error("Error loading users");
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  const toggle = (key, value) => {
    setFilter((prev) => {
      const arr = [...prev[key]];
      const i = arr.indexOf(value);
      if (i === -1) arr.push(value);
      else arr.splice(i, 1);
      return { ...prev, [key]: arr, page: 1 };
    });
  };

  return (
    <div>
      <div className="flex gap-2 flex-wrap flex-col">
        <div className="flex items-end space-x-3 flex-wrap">
          <div className="flex-1 space-y-1 min-w-[200px]">
            <DebounceInput
              debounce={300}
              className="input"
              placeholder="Search (name, email, skills, etc...)"
              value={filter.search}
              onChange={(e) => setFilter((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
            />
          </div>

          <div className="block">
            <select
              className="input"
              name="status"
              value={filter.status}
              onChange={(e) => {
                e.persist();
                if (e.target.value === "all") setFilter((prev) => ({ ...prev, status: "" }));
                else setFilter((prev) => ({ ...prev, status: e.target.value }));
              }}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <Create />
        </div>

        <div className="flex w-full gap-2 mt-2">
          <div>
            <div className="space-y-0.5">
              <label className="text-xs font-medium text-gray-700">Organisation</label>
              <div className="flex flex-wrap gap-1 bg-white border border-gray-200 p-1.5 rounded-md shadow-sm">
                {["Le Collectif", "Selego BV", "Selegoloco", "Walego", "Tirana", "Konstrukt Shpk"].map((org) => (
                  <label key={org} className="flex items-center hover:bg-gray-50 px-1.5 py-0.5 rounded transition-colors cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={filter.organisations.includes(org)}
                      onChange={() => toggle("organisations", org)}
                      className="mr-1.5 accent-blue-500 h-3 w-3"
                    />
                    <span>{org}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="space-y-0.5">
              <label className="text-xs font-medium text-gray-700">Contract Type</label>
              <div className="flex flex-wrap gap-1 bg-white border border-gray-200 p-1.5 rounded-md shadow-sm">
                {["CDI", "CDD", "FREELANCE", "VIE", "INTERNSHIP", "APPRENTICESHIP", "Me kohe te plote"].map((c) => (
                  <label key={c} className="flex items-center hover:bg-gray-50 px-1.5 py-0.5 rounded transition-colors cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={filter.contracts.includes(c)}
                      onChange={() => toggle("contracts", c)}
                      className="mr-1.5 accent-blue-500 h-3 w-3"
                    />
                    <span>{c === "FREELANCE" ? "Freelance" : c === "INTERNSHIP" ? "Internship" : c === "APPRENTICESHIP" ? "Apprenticeship" : c}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="space-y-0.5">
              <label className="text-xs font-medium text-gray-700">Role</label>
              <div className="flex flex-wrap gap-1 bg-white border border-gray-200 p-1.5 rounded-md shadow-sm">
                {[
                  ["user", "User"],
                  ["admin", "Admin"],
                  ["applicant", "Applicant"],
                  ["client", "Client"],
                  ["super_admin", "Super Admin"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center hover:bg-gray-50 px-1.5 py-0.5 rounded transition-colors cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={filter.roles.includes(key)}
                      onChange={() => toggle("roles", key)}
                      className="mr-1.5 accent-blue-500 h-3 w-3"
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-1">
          <span className="text-xs font-normal text-gray-500">
            <span className="text-sm font-medium text-gray-700">{users?.length}</span> of {total}
          </span>
        </div>
      </div>

      <Users loading={loading} users={users} setUsers={setUsers} />
      <Pagination
        per_page={filter.per_page}
        total={total}
        onNext={() => setFilter((f) => ({ ...f, page: +f.page + 1 }))}
        onPrevious={() => setFilter((f) => ({ ...f, page: +f.page - 1 }))}
        currentPage={filter.page}
      />
    </div>
  );
}

const Users = ({ loading, users, setUsers }) => {
  if (loading) return <Loader />;
  if (!users?.length) return <div className="mt-4 text-center text-gray-500">No users found</div>;

  return (
    <div className="overflow-x-auto mt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 py-1 gap-5">
        {users?.map((hit) => (
          <UserCard key={hit._id} user={hit} setUser={(user) => setUsers((prev) => prev.map((u) => (u._id === user._id ? user : u)))} />
        ))}
      </div>
    </div>
  );
};

const Create = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({ name: "", email: "", role: "user", description: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const values = { ...formValues, status: "active", application: "accounting" };
      const res = await api.post("/user/invite", values);
      if (!res.ok) throw res;
      toast.success("User created successfully!");
      setOpen(false);
      navigate(`/people/${res.data._id}`);
    } catch (e) {
      console.log(e);
      toast.error("Failed to create user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <button className="btn blue-btn hover:bg-blue-600 transition-colors" onClick={() => setOpen(true)}>
        Create new user
      </button>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50" onClick={() => setOpen(false)}>
          <div className="w-full md:w-[600px] bg-white p-6 rounded-lg shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-4">Create New User</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" name="name" value={formValues.name} onChange={handleChange} required />
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" name="email" type="email" value={formValues.email} onChange={handleChange} required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" name="role" value={formValues.role} onChange={handleChange}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="applicant">Applicant</option>
                  <option value="client">Client</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea name="description" value={formValues.description} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]" />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" onClick={() => setOpen(false)}>
                  Cancel
                </button>
                <LoadingButton className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" loading={isSubmitting} type="submit">
                  Create User
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* SHARED FETCH FOR ORGANIGRAM + OFFICE VIEWS                          */
/* (active employees only, BU-scoped, manager tree)                    */
/* ------------------------------------------------------------------ */

function useEmployeeTree() {
  const organisation = useStore((state) => state.organisation);
  const businessUnit = useSelector((state) => state.Auth.businessUnit);
  const [tree, setTree] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, [businessUnit, organisation]);

  async function fetchEmployees() {
    try {
      const query = {
        role: ["user", "admin", "super_admin"],
        contracts: ["CDI", "CDD", "VIE", "INTERNSHIP", "APPRENTICESHIP", "FREELANCE", "Me kohe te plote"],
        status: "active",
      };
      if (businessUnit?._id && businessUnit._id !== "ALL") {
        query.business_unit_id = businessUnit._id;
      } else if (organisation?._id && organisation._id !== "ALL") {
        query.organisation_id = organisation._id;
      }

      const { data } = await api.post(`/user/search`, query);
      let arr = (data.users || []).map((e) => ({ ...e, children: [] }));
      const items = arr.reduce((acc, e) => {
        acc[e._id] = { ...e, children: [] };
        return acc;
      }, {});
      const built = arr.reduce((acc, e) => {
        if (e.manager_id && items[e.manager_id]) items[e.manager_id].children.push(items[e._id]);
        else acc.push(items[e._id]);
        return acc;
      }, []);
      setTree(built);
    } catch (error) {
      console.error(error);
      toast.error("Error while fetching data");
    }
  }

  return { tree, organisation };
}

const flattenUsers = (users) => {
  const flat = [];
  const walk = (u) => {
    flat.push(u);
    (u.children || []).forEach(walk);
  };
  users.forEach(walk);
  return flat;
};

const countTotalReports = (user) => {
  let total = user.children ? user.children.length : 0;
  if (user.children) user.children.forEach((c) => (total += countTotalReports(c)));
  return total;
};

/* ------------------------------------------------------------------ */
/* ORGANIGRAM VIEW                                                     */
/* ------------------------------------------------------------------ */

function OrganigramView() {
  const { tree, organisation } = useEmployeeTree();
  const [zoom, setZoom] = useState(1);
  const navigate = useNavigate();

  if (!tree) return <Loader />;

  const MiniCard = ({ user }) => {
    const location = useLocation();
    const directReports = user.children ? user.children.length : 0;
    const totalReports = countTotalReports(user);
    const role = user.job_title || user.position || "";
    const office = user.office || "";

    return (
      <div
        className="bg-white border border-gray-200 rounded-lg transition-colors"
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#fff48b")}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
        style={{ width: "130px", padding: "12px" }}>
        <div className="flex flex-col items-center">
          <div className="relative mb-2">
            <img src={user.avatar} className="w-10 h-10 rounded-full object-cover border-2 border-gray-100" />
            {directReports > 0 && (
              <div className="absolute -top-1 -right-1 flex flex-col gap-0.5">
                <span className="text-gray-900 text-[9px] font-medium min-w-[18px] h-4 px-1 rounded-full flex items-center justify-center" style={{ backgroundColor: "#fff48b" }}>
                  {directReports}
                </span>
                {totalReports > directReports && (
                  <span className="text-gray-700 text-[8px] font-medium min-w-[18px] h-3 px-1 rounded-full flex items-center justify-center bg-gray-200">{totalReports}</span>
                )}
              </div>
            )}
          </div>
          <div className="text-xs font-medium text-gray-900 text-center truncate w-full">{user.name}</div>
          {role && <div className="text-[10px] text-gray-600 truncate w-full text-center mt-0.5">{role}</div>}
          {office && <div className="text-[9px] text-gray-400 truncate w-full text-center mt-0.5">{office}</div>}
          <div className="flex gap-1.5 mt-2.5 w-full">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const query = new URLSearchParams(location.search);
                query.set("user_modal_id", user._id);
                navigate({ search: query.toString() });
              }}
              className="flex-1 py-1 text-[9px] bg-gray-100 hover:bg-gray-200 text-gray-700 rounded flex items-center justify-center gap-1"
              title="Open Profile">
              <FaUser size={9} /> Profile
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/management/${user._id}`, "_blank");
              }}
              className="flex-1 py-1 text-[9px] text-gray-900 rounded flex items-center justify-center gap-1 transition-colors"
              style={{ backgroundColor: "#fff48b" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fff176")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff48b")}
              title="Open Management">
              <FaChartLine size={9} /> Manage
            </button>
          </div>
        </div>
      </div>
    );
  };

  const DisplayTree = ({ tree }) => {
    if (!tree.children || !tree.children.length) return null;
    return (
      <>
        {tree.children.map((child) => (
          <TreeNode key={child._id} label={<div className="flex justify-center"><MiniCard user={child} /></div>}>
            <DisplayTree tree={child} />
          </TreeNode>
        ))}
      </>
    );
  };

  const orgLabel = (() => {
    const name = organisation?.name || "team";
    const lower = name.toLowerCase();
    if (lower.includes("selego")) return { emoji: "👔", display: "selego" };
    if (lower.includes("waalego")) return { emoji: "🌊", display: "waalego" };
    if (lower.includes("tirana")) return { emoji: "🇦🇱", display: "tirana" };
    return { emoji: "🏢", display: lower };
  })();

  return (
    <div className="w-full h-[calc(100vh-160px)] flex flex-col bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2 p-2 bg-white border-b sticky top-0 z-10">
        <button onClick={() => setZoom((z) => Math.max(z - 0.1, 0.3))} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded">−</button>
        <span className="text-xs text-gray-600 w-12 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom((z) => Math.min(z + 0.1, 1.5))} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded">+</button>
        <button onClick={() => setZoom(1)} className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded">Reset</button>
        <span className="text-xs text-gray-400 ml-auto">Scroll to pan • Click person to view</span>
      </div>
      <div className="flex-1 overflow-auto p-4">
        <div style={{ transform: `scale(${zoom})`, transformOrigin: "top center", minWidth: "max-content" }}>
          <Tree
            label={
              <div className="text-xs font-medium text-gray-900 px-3 py-1 rounded border border-gray-200" style={{ backgroundColor: "#fff48b" }}>
                <span>{orgLabel.emoji} {orgLabel.display}</span>
              </div>
            }
            lineWidth="1px"
            lineColor="#E5E7EB"
            lineBorderRadius="4px">
            {tree.map((user) => (
              <TreeNode key={user._id} label={<div className="flex justify-center"><MiniCard user={user} /></div>}>
                <DisplayTree tree={user} />
              </TreeNode>
            ))}
          </Tree>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* OFFICE VIEW                                                         */
/* ------------------------------------------------------------------ */

function OfficeView() {
  const { tree } = useEmployeeTree();

  if (!tree) return <Loader />;

  const allUsers = flattenUsers(tree);
  const offices = ["Paris", "Barcelone", "Tirana", "Amsterdam", "Remote"];

  const getOfficeColor = (office) => {
    if (!office) return "border-gray-300 bg-gray-50";
    const o = office.toLowerCase();
    if (o === "paris") return "border-blue-300 bg-blue-50";
    if (o === "amsterdam") return "border-orange-300 bg-orange-50";
    if (o === "barcelone" || o === "barcelona") return "border-yellow-300 bg-yellow-50";
    if (o === "tirana") return "border-red-300 bg-red-50";
    if (o === "remote") return "border-green-300 bg-green-50";
    return "border-gray-300 bg-gray-50";
  };

  const getOfficeTitle = (office) => {
    if (!office) return "No Office";
    const o = office.toLowerCase();
    if (o === "barcelone" || o === "barcelona") return "Barcelona";
    return office;
  };

  const grouped = offices.reduce((acc, o) => ({ ...acc, [o]: [] }), { Other: [] });
  allUsers.forEach((user) => {
    const o = (user.office || "").toLowerCase();
    if (o === "paris") grouped.Paris.push(user);
    else if (o === "amsterdam") grouped.Amsterdam.push(user);
    else if (o === "barcelone" || o === "barcelona") grouped.Barcelone.push(user);
    else if (o === "tirana") grouped.Tirana.push(user);
    else if (o === "remote") grouped.Remote.push(user);
    else grouped.Other.push(user);
  });

  return (
    <div className="w-full space-y-3 pb-4 px-2">
      {offices.map((office) => {
        const officeUsers = grouped[office] || [];
        if (!officeUsers.length) return null;
        return (
          <div key={office} className={`rounded-lg border-2 p-3 ${getOfficeColor(office)}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-bold text-gray-800">{getOfficeTitle(office)}</h2>
              <span className="text-xs font-medium text-gray-600">{officeUsers.length}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {officeUsers.map((user) => (
                <OfficeUserCard key={user._id} user={user} showOffice={false} />
              ))}
            </div>
          </div>
        );
      })}
      {grouped.Other.length > 0 && (
        <div className="rounded-lg border-2 border-gray-300 bg-gray-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-gray-800">Other / No Office</h2>
            <span className="text-xs font-medium text-gray-600">{grouped.Other.length}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {grouped.Other.map((user) => (
              <OfficeUserCard key={user._id} user={user} showOffice={true} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OfficeUserCard({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const directReports = user.children?.length || 0;
  const totalReports = countTotalReports(user);

  return (
    <div
      className="bg-white border border-gray-200 rounded transition-colors cursor-pointer relative w-full"
      style={{ borderColor: "#e5e7eb" }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#fff48b")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
      onClick={() => {
        const query = new URLSearchParams(location.search);
        query.set("user_modal_id", user._id);
        navigate({ search: query.toString() });
      }}>
      {directReports > 0 && (
        <div className="absolute -top-1 -right-1 flex gap-1">
          <span className="text-gray-900 text-[9px] font-medium px-1.5 rounded-full" style={{ backgroundColor: "#fff48b" }}>{directReports}</span>
          {totalReports > directReports && <span className="bg-gray-200 text-gray-700 text-[9px] px-1.5 rounded-full">{totalReports}</span>}
        </div>
      )}
      <div className="flex flex-col items-center p-2">
        <img src={user.avatar} className="w-8 h-8 rounded-full mb-1 border border-gray-100" />
        <div className="text-center w-full">
          <div className="text-xs font-medium text-gray-900 truncate">{user.name}</div>
          {user.job_title && <div className="text-[10px] text-gray-500 truncate mt-0.5">{user.job_title}</div>}
        </div>
      </div>
    </div>
  );
}
